import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import RichEditor from "../components/RichEditor";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Download, Trash2, FileDown } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function downloadPdf(path, filename) {
  const token = localStorage.getItem("marianne_token");
  fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("PDF export failed");
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    })
    .catch(() => toast.error("PDF export failed"));
}

export function DraftsListPage() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/drafts").then((r) => setItems(r.data));
  }, []);
  return (
    <div data-testid="drafts-list-page">
      <PageHeader
        eyebrow="Les Brouillons"
        title="Drafts"
        description="Your works-in-progress — continue where you left off."
        action={
          <Link to="/app/templates">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="drafts-new-btn">
              <FileText size={14} className="mr-1.5" /> New from template
            </Button>
          </Link>
        }
      />
      {items.length === 0 ? (
        <EmptyState icon={FileText} title="No drafts yet" description="Pick a template to generate your first draft." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((d) => (
            <Link key={d.id} to={`/app/drafts/${d.id}`} className="border border-[#E8E3F0] bg-white p-5 hover-lift" data-testid={`drafts-list-item-${d.id}`}>
              <div className="font-serif text-2xl">{d.title}</div>
              <div className="text-xs text-[#4A5568] mt-1">Updated {new Date(d.updated_at).toLocaleString()}</div>
              <div className="mt-3 text-sm text-[#2A0F5A]/70 line-clamp-3" dangerouslySetInnerHTML={{ __html: stripTags(d.content || "").slice(0, 280) }} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function stripTags(s) {
  return (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export default function DraftEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/drafts/${id}`).then((r) => {
      setD(r.data);
      setTitle(r.data.title);
      // Plaintext → wrap each paragraph in <p> for TipTap
      const c = r.data.content || "";
      if (c && !c.trim().startsWith("<")) {
        setContent(
          c
            .split(/\n\n+/)
            .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
            .join(""),
        );
      } else {
        setContent(c);
      }
    });
  }, [id]);

  if (!d) return <div className="label-eyebrow">Loading draft…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/drafts/${id}`, { title, content });
      toast.success("Saved");
    } finally {
      setSaving(false);
    }
  };

  const downloadTxt = () => {
    const blob = new Blob([stripTags(content)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title || "draft"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    downloadPdf(`/api/drafts/${id}/pdf`, `${title || "draft"}.pdf`);
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/drafts/${id}`);
    toast.success("Deleted");
    navigate("/app/drafts");
  };

  return (
    <div data-testid={`draft-editor-${id}`}>
      <button onClick={() => navigate("/app/drafts")} className="text-sm text-[#4A5568] hover:text-[#2A0F5A] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> All drafts
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex-1 min-w-[280px]">
          <Label className="label-eyebrow mb-2 block">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A] font-serif text-2xl h-14" data-testid="draft-title-input" />
        </div>
        <div className="flex items-end gap-2 pb-0.5">
          <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={downloadTxt} data-testid="draft-download-btn"><Download size={14} className="mr-1" />TXT</Button>
          <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={exportPdf} data-testid="draft-pdf-btn"><FileDown size={14} className="mr-1" />PDF</Button>
          <Button variant="outline" className="rounded-none border-[#E8E3F0] text-red-700" onClick={remove} data-testid="draft-delete-btn"><Trash2 size={14} className="mr-1" /> Delete</Button>
          <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={save} disabled={saving} data-testid="draft-save-btn"><Save size={14} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
      <RichEditor value={content} onChange={setContent} placeholder="Begin drafting…" minHeight="560px" testId="draft-editor" />
    </div>
  );
}
